import { addressSchema } from '@/common/schemas/address.schema';
import { nullableOptionSchema, optionSchema } from '@/common/schemas/option.schema';
import { DocumentData } from '@/common/types/document';
import { unFormatCurrency } from '@/common/utils/unFormatters';
import { z } from 'zod';

const geosSchema = z.object({
  value: z.string().optional(),
  type: z.string().optional()
})

export const updateSchema = z.object({
  commercial_manager: optionSchema.optional(),
  commercial_manager_substitute: nullableOptionSchema.optional().nullable(),
  financial_information_attributes: z.object({
    approved_credit: z.string().optional().refine((value) => {
      return value ? unFormatCurrency(`${value}`) <= 100000000 : true
    }, { message: 'O crédito aprovado não pode ser superior a R$100.000.000,00' }),
  }).optional(),
  operational_information_attributes: z.object({
    expected_credit: z.string().optional().refine((value) => {
      return value ? unFormatCurrency(`${value}`) <= 100000000 : true
    }, { message: 'A expectativa de crédito não pode ser superior a R$100.000.000,00' }),
    expected_equipment_quantity: z.string().optional(),
    industry_segment: optionSchema.optional(),
    product_categories: z.array(optionSchema).optional(),
    geos: z.array(geosSchema).optional(),
  }).optional(),
  fiscal_document_information: z.object({
    consumption_observations: z.string().optional().nullable(),
    service_observations: z.string().optional().nullable(),
    seller_cnpj: z.string().optional().nullable(),
    fiscal_document_kind: optionSchema.optional(),
  }).optional().nullable(),
  general_information: z.object({
    cnpj: z.string().optional().nullable(),
    email: z.string().optional().nullable(),
    fantasy_name: z.string().optional().nullable(),
    official_name: z.string().optional().nullable(),
    primary_cnae: z.string().optional().nullable(),
    state_inscription: z.string().optional().nullable(),
    new_company_logo: z.custom<Blob>().optional().nullable(),
  }).optional(),
  address_attributes: addressSchema.optional(),
  contact_for_master_user: z.object({
    value: z.string().optional().nullable(),
  }).optional().nullable(),
  contract: z.object({
    cycle: optionSchema.optional(),
    cycle_start_day: optionSchema.optional(),
    cycle_fixed_interval: z.string().optional()
  }).optional().nullable(),
  printer_machine_id: optionSchema.optional().nullable(),
  fee_rule: z.object({
    special_entries: z.array(
      z.object({
        id: z.string().optional().nullable(),
        target: optionSchema.optional().nullable(),
        fee_template_id: z.string().optional().nullable(),
        value: z.string().optional().nullable(),
        not_use_if_has_special_fee: z.boolean().optional().nullable(),
        execution_priority: z.number().optional().nullable(),
      }).optional().nullable(),
    ).optional().nullable(),
  }).optional().nullable(),
  observations: z.string().optional().nullable()
})

export type PrivateCompanyLeadsFunnelUpdateValidationSchema = z.infer<typeof updateSchema>

export const updateContactSchema = z.object({
  contacts: z.object({
    email: z.string().optional().nullable(),
    full_name: z.string().optional().nullable(),
    role_in_company: z.string().optional().nullable(),
    phone: z.object({
      number: z.string().optional().nullable(),
      kind: z.string().optional().nullable(),
      whatsapp_capability: z.string().optional().nullable(),
    }).optional(),
    responsible_for_company_information: z.boolean().optional().nullable(),
    cpf: z.string().optional().nullable(),
    documents: z.array(
      z.object({
        id: z.string().optional().nullable(),
        cnh: z.string().optional().nullable(),
        file: z.custom<Blob>().optional().nullable(),
        issued_on_date: z.string().optional().nullable(),
        expires_at: z.string().optional().nullable(),
        rg: z.string().optional().nullable(),
        type: z.string().optional().nullable(),
        last_review: z.object({
          description: z.string().optional().nullable(),
          state: z.string().optional().nullable(),
        }).optional().nullable(),
        doc_options: z.custom<DocumentData>().optional().nullable(),
        checked: z.boolean().optional().nullable(),
      }).optional(),
    ),
  }),
})

export type PrivateCompanyLeadsFunnelUpdateContactValidationSchema = z.infer<typeof updateContactSchema>

export const updateContractSchema = z.object({
  contract: z.object({
    duration_in_months: optionSchema.optional().nullable(),
    bill_by: optionSchema.optional().nullable(),
    bill_grouping_mode: optionSchema.optional().nullable(),
    cycle: optionSchema.optional().nullable(),
    cycle_start_day: optionSchema.optional().nullable(),
    cycle_fixed_interval: z.string().optional(),
    closing_to_due_in_days: optionSchema.optional().nullable(),
    cnpj_for_fiscal_document: optionSchema.optional(),
    contract_equipment_categories: z.array(
      z.object({
        id: z.string(),
        equipment_category: z.object({
          id: z.string().optional(),
          display_name: z.string().optional(),
        }),
        quantity: z.string().optional(),
      })
    ).optional().nullable(),
    contract_fees: z.array(
      z.object({
        id: z.string(),
        value: z.string(),
        main_fee: z.boolean().optional().nullable(),
      })
    ).optional().nullable(),
    contract_product_categories: z.array(optionSchema).optional().nullable(),
    credit_granted: z.number().optional().nullable(),
    due_to_suspension_in_days: optionSchema.optional().nullable(),
    file: z.object({
      content_type: z.string().optional().nullable(),
      url: z.string().optional().nullable(),
    }).optional().nullable(),
    fiscal_document_requirement: optionSchema.optional().nullable(),
    geo_geoables: z.array(geosSchema).optional(),
    in_effect_since: z.string().optional().nullable(),
    in_effect_until: z.string().optional().nullable(),
    payment_method: optionSchema.optional().nullable(),
    payment_modality: z.string().optional().nullable(),
  })
})

export type PrivateCompanyLeadsFunnelUpdateContractValidationSchema = z.infer<typeof updateContractSchema>
